'use client';

import React, { useState } from 'react';

// Primitives shared by the calculators under /calculators. Extracted from
// AirbnbSplitCalculator, which was the only one when they were written — every
// comment below records a bug these fixed, so change them with that in mind.

export const OMBRE = 'linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe)';
export const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';

export const money = (n: number) =>
  Number.isFinite(n) && n > 0 ? `$${Math.round(n).toLocaleString('en-US')}` : '$0';

export const num = (s: string) => {
  const n = parseFloat(s.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Round a set of exact shares to whole dollars that still add up to the pool
// they came out of (largest-remainder apportionment). Rounding each share on
// its own leaves the column short or over — six people splitting $2,040 each
// land on .43 of a dollar, and the page then shows a set of numbers that
// doesn't reconcile against the bill. On a bill-splitting site that is the
// detail someone screenshots.
export const apportion = (exact: number[], pool: number): number[] => {
  if (!exact.length) return [];
  const target = Math.round(pool);
  const out = exact.map((v) => Math.floor(v));
  let left = target - out.reduce((a, b) => a + b, 0);
  const byFraction = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  // `left` is bounded by the count in practice; the modulo keeps a pathological
  // input (an empty pool, a stale target) from running off the end.
  for (let k = 0; left > 0; k++, left--) out[byFraction[k % byFraction.length].i]++;
  return out;
};

export const card: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: '16px',
  padding: '18px',
  marginBottom: '14px',
};

export const sendBtn: React.CSSProperties = {
  padding: '13px',
  borderRadius: '10px',
  border: '1px solid rgba(6,223,250,0.45)',
  background: 'rgba(6,223,250,0.10)',
  cursor: 'pointer',
  fontSize: '15px',
  fontWeight: 700,
  fontFamily: FONT,
};

// Ombre needs an explicit text-decoration-color: the underline inherits
// `color`, which background-clip has set to transparent, so it vanishes.
export const cta: React.CSSProperties = {
  background: OMBRE,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
  textDecoration: 'underline',
  textDecorationColor: '#06dffa',
  fontWeight: 600,
};

export const ombreText: React.CSSProperties = {
  background: OMBRE,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};

// Ombre, so the things the reader has to answer are the only coloured text in
// the card — derived readouts stay grey. background-clip needs display:block on
// the element it paints, which a <label> isn't by default.
export const label: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  marginBottom: '6px',
  display: 'block',
  width: 'fit-content',
  background: OMBRE,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};

export const input: React.CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderRadius: '10px',
  padding: '11px 13px',
  color: 'white',
  // 16px keeps iOS from zooming the viewport when the field takes focus.
  fontSize: '16px',
  fontFamily: FONT,
  outline: 'none',
  boxSizing: 'border-box',
};

// Text field with an ombre outline while focused.
//
// The ring is a masked overlay, not a gradient sitting behind the input. A
// padded wrapper only works when the input's own background is opaque enough to
// hide it — and this one is deliberately near transparent, so the gradient
// showed through the whole field instead of just its edge. The xor/exclude mask
// punches out everything inside the 1px padding, leaving the border and nothing
// else. `border-image` isn't an option: it doesn't follow border-radius, so the
// corners square off in every engine.
export const Field: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  numeric?: boolean;
  center?: boolean;
  onBlur?: () => void;
  width?: string;
  prefix?: string;
  ariaLabel?: string;
}> = ({ value, onChange, placeholder, numeric, center, onBlur, width, prefix, ariaLabel }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: '11px',
        width: width ?? '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '11px',
          padding: '1px',
          background: focused ? OMBRE : 'rgba(255,255,255,0.13)',
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          maskComposite: 'exclude',
          pointerEvents: 'none',
          boxSizing: 'border-box',
        }}
      />
      <input
        style={{
          ...input,
          background: 'rgba(0,0,0,0.10)',
          textAlign: center ? 'center' : 'left',
          padding: center ? '11px 4px' : `11px 13px 11px ${prefix ? 24 : 13}px`,
          position: 'relative',
        }}
        inputMode={numeric ? 'decimal' : undefined}
        placeholder={placeholder}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          onBlur?.();
        }}
      />
      {/* Painted over the input rather than typed into it: keeping it out of
          `value` means the parser never has to strip it back off, and the field
          can still be cleared to empty. pointer-events:none so tapping the
          glyph still focuses the field under it. */}
      {prefix && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: '13px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '16px',
            lineHeight: 1,
            color: value ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          {prefix}
        </span>
      )}
    </div>
  );
};

// One connected pill with a divider, rather than separate pills with a gap —
// same idiom as the app's segmented controls, and it survives a ~60px table
// column where two separate pills would not.
export const Seg: React.FC<{
  value: boolean;
  onChange: (v: boolean) => void;
  yes: string;
  no: string;
}> = ({ value, onChange, yes, no }) => (
  <div className="abnb-seg">
    <button type="button" data-on={value ? '1' : '0'} onClick={() => onChange(true)}>
      {yes}
    </button>
    <button type="button" data-on={!value ? '1' : '0'} onClick={() => onChange(false)}>
      {no}
    </button>
  </div>
);

// The same control for more than two options. Kept separate from Seg rather
// than replacing it: Seg's boolean API is what a yes/no column wants, and
// widening it would have meant touching every existing call site.
export function SegChoice<T extends string | number>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
}) {
  return (
    <div className="abnb-seg">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          data-on={value === o.value ? '1' : '0'}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Native <select> rather than a pill row: a seven-option scale has no chance of
// fitting a table column, and the OS picker is better on a phone than anything
// rebuildable here. Styled to match Field — same radius, same hairline, same
// near-transparent fill.
export function Picker<T extends string | number>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
  ariaLabel?: string;
}) {
  return (
    <select
      className="abnb-pick"
      aria-label={ariaLabel}
      value={String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        const hit = options.find((o) => String(o.value) === raw);
        if (hit) onChange(hit.value);
      }}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// Real CSS rather than inline styles: the tables need media queries to stay
// legible at 375px, and inline styles can't carry one. The abnb- prefix is
// historical — these are shared by every calculator now.
export const SHARED_CSS = `
.abnb-top{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;align-items:end}
.abnb-people{display:flex;align-items:center;gap:1px}
.abnb-step{width:34px;height:38px;flex:none;background:transparent;border:none;padding:0;
  cursor:pointer;color:rgba(255,255,255,0.72);font-size:22px;line-height:1;font-family:inherit}
/* The count field is pushed right by the − stepper, so its label has to clear
   the same distance or it hangs left of the input the other two sit over. */
.abnb-people-l{padding-left:35px}
.abnb-send{display:flex;gap:10px}
.abnb-copy{order:1;flex:1}
.abnb-share{order:2;flex:1}
.abnb-copy-icon{display:none;align-items:center;justify-content:center}
@media (max-width:430px){
  /* Share carries the label and the width; Copy collapses to a glyph on its
     right, which is where a thumb already is. */
  .abnb-send[data-share="1"] .abnb-share{order:1}
  .abnb-send[data-share="1"] .abnb-copy{order:2;flex:none;width:54px;padding-left:0;padding-right:0}
  .abnb-send[data-share="1"] .abnb-copy-full{display:none}
  .abnb-send[data-share="1"] .abnb-copy-icon{display:inline-flex}
  .abnb-top{gap:7px}
  .abnb-people{gap:0}
  /* A third of a 291px card is ~90px; two 34px steppers would leave the count
     field 22px. */
  .abnb-step{width:20px;font-size:17px}
  .abnb-people-l{padding-left:20px}
}
.abnb-h{font-size:11px;font-weight:600;color:rgba(255,255,255,0.48);text-align:center;
  line-height:1.25}
.abnb-h.abnb-l{text-align:left}
.abnb-h.abnb-r{text-align:right}
.abnb-rule{grid-column:1/-1;height:1px;background:rgba(255,255,255,0.07)}
.abnb-p{font-size:12.5px;font-weight:700;color:rgba(255,255,255,0.5)}
/* justify-self:end rather than text-align:right so the cell shrink-wraps the
   digits. */
.abnb-amt{font-size:15px;font-weight:700;white-space:nowrap;justify-self:end;color:#fff}
.abnb-i{margin-left:3px;width:14px;height:14px;flex:none;border-radius:50%;padding:0;
  border:1px solid rgba(255,255,255,0.32);background:transparent;
  color:rgba(255,255,255,0.55);font-size:9.5px;line-height:12px;font-weight:700;
  font-family:inherit;cursor:pointer;vertical-align:middle}
.abnb-i[data-open="1"]{border-color:rgba(6,223,250,0.7);color:#7fe9ff;
  background:rgba(6,223,250,0.14)}
.abnb-info{grid-column:1/-1;margin:2px 0 4px;padding:10px 12px;border-radius:10px;
  background:rgba(6,223,250,0.07);border:1px solid rgba(6,223,250,0.20);
  font-size:12px;line-height:1.6;color:rgba(255,255,255,0.78);text-align:left}
/* width:max-content rather than filling the column — the pill is only as wide
   as its words need, and the leftover column width becomes the gutter between
   controls instead of stretching them. */
.abnb-seg{display:flex;width:max-content;max-width:100%;justify-self:center;
  height:30px;border:1px solid rgba(255,255,255,0.14);border-radius:999px;overflow:hidden}
.abnb-seg button{flex:none;padding:0 9px;border:none;cursor:pointer;
  background:rgba(255,255,255,0.05);color:rgba(255,255,255,0.5);
  font-size:11px;font-weight:600;font-family:inherit;white-space:nowrap}
.abnb-seg button+button{border-left:1px solid rgba(255,255,255,0.12)}
.abnb-seg button[data-on="1"]{background:rgba(255,255,255,0.16);color:#fff}
/* font-size needs !important to clear the global "select{font-size:16px
   !important}" iOS anti-zoom floor in styles/globals.css. Opting out is safe on
   the same grounds the receipt-edit fields already do it on: the layout sets
   maximumScale:1, so focus can't trigger auto-zoom regardless. Without this the
   picker renders at 16px and "Private" truncates to "Privat" inside a 70px
   column. */
.abnb-pick{width:100%;min-width:0;height:30px;border-radius:999px;padding:0 20px 0 9px;
  border:1px solid rgba(255,255,255,0.14);background:rgba(255,255,255,0.05);color:#fff;
  font-size:11px!important;font-weight:600;font-family:inherit;cursor:pointer;
  text-overflow:ellipsis;
  -webkit-appearance:none;appearance:none;
  background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='rgba(255,255,255,0.5)' stroke-width='1.6' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 8px center}
/* The picker inherits the page's dark theme, but the OPEN list is drawn by the
   OS — on a light-mode desktop that list paints white-on-white without this. */
.abnb-pick option{color:#111;background:#fff}
@media (max-width:430px){
  .abnb-h{font-size:9.5px}
  .abnb-seg{height:28px}
  .abnb-seg button{font-size:10px;padding:0 4px}
  .abnb-pick{height:28px;font-size:10px!important;padding:0 16px 0 6px;
    background-position:right 5px center}
  .abnb-amt{font-size:13.5px}
  .abnb-p{font-size:11.5px}
}
`;
